import React, { Suspense, useState, useEffect, Component } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import './App.css';

// i18n
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';

// Auth
import { verifyAuth } from './redux/authSlice';
import { fetchSettings } from './redux/settingsSlice';
import ProtectedRoute from './dashboard/ProtectedRoute';

// Layout Components
import Navbar from './components/layouts/navbar';
import Footer from './components/layouts/footer';
import MovingMessage from './components/menus/movingMsg';
import Loading from './components/layouts/loading';
import ScrollButton from './components/menus/scrollButton';

// Dashboard Components
import AdminLayout from './dashboard/adminLayout';
import AdminLogin from './dashboard/adminLogin';
import AddProduct from './dashboard/addProduct';
import EditProduct from './dashboard/editProduct';
import AddCategory from './dashboard/addCategory';
import ManageOrders from './dashboard/manageOrders';
import ManageCategories from './dashboard/manageCategories';
import ManageProducts from './dashboard/manageProducts';
import ManageComments from './dashboard/manageComments';
import DashboardSettings from './dashboard/settings';
import DashboardHome from './dashboard/dashboardHome';

// Page Components
import ProductsByCategory from './pages/productsByCategory ';
import ArticleDetail from './pages/singleArticle';

const Home        = React.lazy(() => import('./pages/home'));
const AllProducts = React.lazy(() => import('./pages/allProducts'));
const SingleProduct = React.lazy(() => import('./pages/singleProduct'));
const Cart        = React.lazy(() => import('./pages/cart'));
const Checkout    = React.lazy(() => import('./pages/checkout'));

// ── Error Boundary ─────────────────────────────────────────────────────────
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('App Error:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4 text-center bg-white">
          <div>
            <p className="text-6xl font-black text-emerald-600 mb-4">!</p>
            <h1 className="text-xl font-black text-gray-900 mb-2">حدث خطأ غير متوقع</h1>
            <p className="text-gray-400 font-bold mb-6 text-sm">يرجى تحديث الصفحة أو العودة للرئيسية</p>
            <button
              onClick={() => { this.setState({ hasError: false }); window.location.href = '/'; }}
              className="px-8 py-3 bg-emerald-600 text-white rounded-2xl font-black hover:bg-emerald-700 transition-all"
            >
              العودة للرئيسية
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Scroll restoration ─────────────────────────────────────────────────────
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// ── App shell ──────────────────────────────────────────────────────────────
const AppContent = () => {
  const location = useLocation();
  const isAdminPath = location.pathname.startsWith('/admin');
  const { t, dir } = useLanguage();

  return (
    <>
      <ScrollToTop />
      {!isAdminPath && (
        <>
          <MovingMessage />
          <Navbar />
        </>
      )}

      <Suspense fallback={<Loading />}>
        <Routes>
          {/* CLIENT ROUTES */}
          <Route path="/"                        element={<Home />} />
          <Route path="/cart"                    element={<Cart />} />
          <Route path="/products"                element={<AllProducts />} />
          <Route path="/articles/:id"            element={<ArticleDetail />} />
          <Route path="/checkout"                element={<Checkout />} />
          <Route path="/products/category/:slug" element={<ProductsByCategory />} />
          <Route path="/products/:slug"          element={<SingleProduct />} />

          {/* ADMIN LOGIN — public */}
          <Route path="/admin/login" element={<AdminLogin />} />

          {/* ADMIN DASHBOARD — protected */}
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index                        element={<DashboardHome />} />
            <Route path="orders"                element={<ManageOrders />} />
            <Route path="products"              element={<ManageProducts />} />
            <Route path="addProduct"            element={<AddProduct />} />
            <Route path="edit-product/:slug"    element={<EditProduct />} />
            <Route path="categories"            element={<ManageCategories />} />
            <Route path="addCategory"           element={<AddCategory />} />
            <Route path="comments"              element={<ManageComments />} />
            <Route path="settings"              element={<DashboardSettings />} />
          </Route>

          <Route path="*" element={
            <div className="min-h-screen flex flex-col items-center justify-center bg-white text-center px-4" dir={dir}>
              <p className="text-8xl font-black text-emerald-600 mb-4">404</p>
              <h1 className="text-2xl font-black text-gray-900 mb-2">{t('notFound.title')}</h1>
              <p className="text-gray-400 font-bold mb-8 text-sm">{t('notFound.msg')}</p>
              <a href="/" className="px-8 py-3.5 bg-emerald-600 text-white rounded-2xl font-black hover:bg-emerald-700 transition-all text-sm">
                {t('notFound.back')}
              </a>
            </div>
          } />
        </Routes>
      </Suspense>

      {!isAdminPath && <Footer />}
      {!isAdminPath && <ScrollButton />}
    </>
  );
};

function App() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initialize = async () => {
      await Promise.all([
        dispatch(verifyAuth()),
        dispatch(fetchSettings()),
        new Promise(resolve => setTimeout(resolve, 300)),
      ]);
      setLoading(false);
    };
    initialize();
  }, [dispatch]);

  if (loading) return <Loading />;

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <div className="App bg-tertiaryWhite text-textParagraphColor min-h-screen">
          <Router>
            <AppContent />
          </Router>
        </div>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
